import { useTranslation } from "react-i18next";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import {
  AssessmentNetworkErrors,
  useAssessment,
  useUpdateAssessment
} from "./assessmentUtils";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { HelpText } from "../../common-ui/HelpText";
import { InputType, UpdateAssessmentInput } from "../../API";
import { useAppSelector } from "../../app/hooks";
import { selectUserCognitoGroups } from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";

const enum AssessmentActions {
  publish = "publish",
  archive = "archive"
}

const pageDetails: PageWrapperProps = {
  className: "Assessment",
  headerText: "Assessment"
};

export const AssessmentDefinition = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUsersPermissions = useAppSelector(selectUserCognitoGroups);
  const isSysAdmin = currentUsersPermissions?.includes(
    CognitoUserGroups.SysAdmins
  );
  const { assessment, status, error, getAssessment } = useAssessment(id);
  const { updateAssessment, status: updateAssessmentStatus } =
    useUpdateAssessment();

  const handleButtonClick = async (action: AssessmentActions) => {
    if (!assessment) return;
    if (action === AssessmentActions.publish) {
      console.log("Publish Assessment");
      const input: UpdateAssessmentInput = {
        published: true,
        id: assessment.id
      };
      await updateAssessment(input);
    } else if (action === AssessmentActions.archive) {
      console.log("Archive Assessment");
      const input: UpdateAssessmentInput = {
        archived: true,
        id: assessment.id
      };
      await updateAssessment(input);
    } else return;
    await getAssessment(assessment.id);
  };

  if (status === "idle") {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Awaiting data...")}</h2>
      </PageWrapper>
    );
  }

  if (status === "loading") {
    return (
      <PageWrapper {...pageDetails}>
        <CircularProgress />
      </PageWrapper>
    );
  }

  if (status === "failed" || error) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t("Error")}</h2>
        <h3>{t(AssessmentNetworkErrors.FetchAssessmentError)}</h3>
        {error && <p>{error}</p>}
      </PageWrapper>
    );
  }

  if (status === "succeeded" && !assessment) {
    return (
      <PageWrapper {...pageDetails}>
        <h2>{t(AssessmentNetworkErrors.AssessmentNotFound)}</h2>
      </PageWrapper>
    );
  }

  const assessmentInstrumentQuestions =
    assessment?.instrument?.questions?.items;
  // Dev note: If sortingIndex is optional, we'll have to develop a way to not need these
  const sortedInstrumenQuestions = assessmentInstrumentQuestions?.sort((a, b) =>
    a?.sortingIndex && b?.sortingIndex && a.sortingIndex < b.sortingIndex
      ? -1
      : 1
  );
  if (status === "succeeded" && assessment) {
    return (
      <PageWrapper {...pageDetails}>
        <ul>
          <li>
            <b>{`Title: ${assessment.title}`}</b>
            <div>
              <button
                disabled={true /*!isSysAdmin || isPublished*/}
                onClick={() => handleButtonClick(AssessmentActions.publish)}
              >
                {t("Publish")}
              </button>
              <button
                disabled={true /*isArchived || !isSysAdmin || !isPublished*/}
                onClick={() => handleButtonClick(AssessmentActions.archive)}
              >
                {t("Archive")}
              </button>
            </div>
          </li>
          <li>{`Sponsor: ${
            assessment.sponsor?.name || "No sponsor specified."
          }`}</li>
          <li>{`Description: ${assessment.description}`}</li>
          <li>{`Expiration Duration (in hours): ${assessment.expirationDurationHours}`}</li>
          <li>
            {`Activities:`}
            <ul>
              {assessment.activities?.items.map((activity) => {
                return (
                  <li>
                    {`Title: ${activity?.title}`}
                    <ul>
                      <li>{`Description: ${activity?.description}`}</li>
                    </ul>
                  </li>
                );
              })}
            </ul>
          </li>
          <li>
            {`Instrument`}
            {isSysAdmin && (
              <div>
                <button
                  disabled={true /*!isSysAdmin || isPublished*/}
                  onClick={() =>
                    navigate(`/instruments/${assessment.instrument?.id}/edit`)
                  }
                >
                  {t("Edit Instrument")}
                </button>
              </div>
            )}
            <ul>
              <li>{`Title: ${assessment.instrument?.title}`}</li>
              <li>{`Description: ${assessment.instrument?.description}`}</li>
              <li>
                {`Questions`}
                <ul>
                  {sortedInstrumenQuestions?.map((question) => {
                    const answerOptions = question?.answerOptions?.items;
                    // Dev note: If sortingIndex is optional, we'll have to develop a way to not need these
                    const sortedAnswerOptions = answerOptions?.sort((a, b) =>
                      a?.sortingIndex &&
                      b?.sortingIndex &&
                      a.sortingIndex < b.sortingIndex
                        ? -1
                        : 1
                    );
                    return (
                      <>
                        <li>{`Title: ${question?.title}`}</li>
                        <ul>
                          <li>{`Sorting Index: ${question?.sortingIndex}`}</li>
                          <li>{`Default Prompt: ${question?.defaultPrompt}`}</li>
                          <li>{`Self Assessment Prompt: ${question?.selfAssessmentPrompt}`}</li>
                          <li>
                            <HelpText text={question?.helpText || ""} />
                          </li>
                          <li>{`Input Type: ${question?.inputType}`}</li>
                          <li>
                            {`Answer Options ${
                              question?.inputType === InputType.Feedback
                                ? "(n/a - Feedack)"
                                : ""
                            }`}
                            <ul>
                              {sortedAnswerOptions?.map((answerOption) => {
                                return (
                                  <>
                                    <li>{`Anchor: ${answerOption?.anchor}`}</li>
                                    <ul>
                                      <li>{`Value: ${answerOption?.value}`}</li>
                                      <li>{`Sorting Index: ${answerOption?.sortingIndex}`}</li>
                                      <li>
                                        <HelpText
                                          text={answerOption?.helpText || ""}
                                        />
                                      </li>
                                    </ul>
                                  </>
                                );
                              })}
                            </ul>
                          </li>
                        </ul>
                      </>
                    );
                  })}
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </PageWrapper>
    );
  }
  return <div>No criteria met</div>;
};
